import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import logoLight from '../assets/imgs/Logo_principal.webp';
import logoDark from '../assets/imgs/Logo_2.webp';
import { CONTACT, NAV_ITEMS, buildWhatsAppUrl } from '../data/site';
import { Icon } from './Icon';

const TOP_LIMIT = 12;
const HIDE_AFTER = 120;
const SCROLL_DELTA = 6;

export function Header() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [atTop, setAtTop] = useState(true);
  const lastY = useRef(0);
  const raf = useRef(0);
  const closeRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setOpen(false);
    setVisible(true);
    setAtTop(window.scrollY <= TOP_LIMIT);
  }, [pathname]);

  useEffect(() => {
    const update = () => {
      const y = Math.max(0, window.scrollY);
      const delta = y - lastY.current;
      const top = y <= TOP_LIMIT;
      setAtTop(top);

      if (top || open) setVisible(true);
      else if (delta > SCROLL_DELTA && y > HIDE_AFTER) setVisible(false);
      else if (delta < -SCROLL_DELTA) setVisible(true);

      lastY.current = y;
      raf.current = 0;
    };
    const onScroll = () => {
      if (!raf.current) raf.current = requestAnimationFrame(update);
    };
    lastY.current = window.scrollY;
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
      if (event.key !== 'Tab' || !drawerRef.current) return;
      const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const mode = atTop ? 'top' : 'scrolled';

  return (
    <>
      <header className={`site-header site-header--${mode}${visible ? ' is-visible' : ' is-hidden'}${open ? ' is-menu-open' : ''}`}>
        <div className="site-header__inner">
          <NavLink to="/" className="site-header__brand" aria-label="Corporación Indemniza, inicio">
            <img className="site-header__logo site-header__logo--light" src={logoLight} alt="Corporación Indemniza" />
            <img className="site-header__logo site-header__logo--dark" src={logoDark} alt="" aria-hidden="true" />
          </NavLink>

          <div className="site-header__actions">
            <a className="header-wa" href={buildWhatsAppUrl()} target="_blank" rel="noopener noreferrer" aria-label={`Escribir por WhatsApp al ${CONTACT.phoneDisplay}`}>
              <Icon name="whatsapp" size={21} />
              <span>{CONTACT.phoneDisplay}</span>
            </a>
            <button className="header-menu" type="button" aria-label="Abrir menú" aria-expanded={open} aria-controls="site-drawer" onClick={() => setOpen(true)}>
              <Icon name="menu" size={31} />
            </button>
          </div>
        </div>
      </header>

      <button className={`drawer-backdrop${open ? ' is-open' : ''}`} type="button" aria-label="Cerrar menú" tabIndex={open ? 0 : -1} onClick={() => setOpen(false)} />

      <aside ref={drawerRef} id="site-drawer" className={`site-drawer${open ? ' is-open' : ''}`} aria-hidden={!open} aria-label="Navegación principal">
        <div className="site-drawer__top">
          <img src={logoLight} alt="Corporación Indemniza" />
          <button ref={closeRef} type="button" aria-label="Cerrar menú" onClick={() => setOpen(false)}>
            <Icon name="x" size={29} />
          </button>
        </div>

        <nav className="site-drawer__nav">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)} className={({ isActive }: { isActive: boolean }) => (isActive ? 'is-active' : undefined)}>
              <span>{item.label}</span>
              <Icon name="arrowRight" size={18} />
            </NavLink>
          ))}
        </nav>

        <div className="site-drawer__contact">
          <a className="drawer-wa" href={buildWhatsAppUrl()} target="_blank" rel="noopener noreferrer">
            <Icon name="whatsapp" size={20} /> Escríbenos por WhatsApp
          </a>
          <a href={`mailto:${CONTACT.email}`}><Icon name="mail" size={17} /> {CONTACT.email}</a>
          <p><Icon name="mapPin" size={17} /> {CONTACT.shortAddress}</p>
        </div>
      </aside>
    </>
  );
}
